import type { Lane } from '@botguide/shared';
import { LANE_LABELS } from '@botguide/shared';

import { cn } from '@/lib/cn';

import { Badge } from './Badge';

/**
 * Lane colours are tokens keyed by the enum value, so this is the only place
 * that needs to know a lane has a colour at all. Add a lane to the shared
 * enums, add `--lane-<value>` to theme.css, and every lane badge, rail and
 * column header in the app picks it up.
 */
export function laneColour(lane: Lane): string {
  return `var(--lane-${lane})`;
}

export function laneColours(lane: Lane): { fill: string; ink: string; edge: string } {
  return {
    fill: `var(--lane-${lane}-soft)`,
    ink: `var(--lane-${lane}-ink)`,
    edge: `color-mix(in srgb, var(--lane-${lane}) 28%, transparent)`,
  };
}

export function LaneBadge({
  lane,
  size = 'md',
  className,
}: {
  lane: Lane;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <Badge tone="custom" colours={laneColours(lane)} dot size={size} className={className}>
      {LANE_LABELS[lane]}
    </Badge>
  );
}

/**
 * The heading above a board column: the lane name, its count, and a rule in
 * the lane colour thick enough to read from across the room.
 */
export function LaneHeading({
  lane,
  count,
  points,
  className,
}: {
  lane: Lane;
  count: number;
  points?: number;
  className?: string;
}) {
  return (
    <div className={cn('pb-2', className)}>
      <div className="h-[3px] w-full rounded-full" style={{ backgroundColor: laneColour(lane) }} />
      <div className="mt-2 flex items-baseline justify-between gap-2">
        <h3 className="font-display text-body font-semibold tracking-tight">
          {LANE_LABELS[lane]}
        </h3>
        <span className="font-mono text-nano text-subtle tnum">
          {count}
          {points === undefined ? '' : ` · ${points}p`}
        </span>
      </div>
    </div>
  );
}
